<template>
  <!-- Compact inline badge within header - similar to ActingContextBanner -->
  <div v-if="isEditingPlaylist && editContext" class="playlist-edit-badge">
    <div class="playlist-edit-badge__pill">
      <!-- Playlist cover thumbnail -->
      <img
        v-if="playlistCover"
        :src="playlistCover"
        :alt="playlistName"
        class="playlist-edit-badge__cover"
      />
      <v-icon v-else size="14" class="playlist-edit-badge__icon">
        mdi-playlist-music
      </v-icon>

      <!-- Status text -->
      <span class="playlist-edit-badge__text">
        <span class="playlist-edit-badge__prefix">Editing: </span><strong>{{ playlistName }}</strong>
      </span>

      <!-- Songs added count -->
      <span v-if="songsAdded > 0" class="playlist-edit-badge__count">
        {{ songsAdded }} added
      </span>

      <!-- View playlist link -->
      <button
        class="playlist-edit-badge__view"
        title="View playlist"
        @click.stop="viewPlaylist"
      >
        <v-icon size="14">mdi-open-in-new</v-icon>
      </button>

      <!-- Exit button -->
      <button
        class="playlist-edit-badge__exit"
        title="Exit Edit Mode"
        @click="exitEditMode"
      >
        <v-icon size="14">mdi-close</v-icon>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { usePlaylistEditMode } from "@/composables/usePlaylistEditMode";

const router = useRouter();

const {
  editContext,
  isEditingPlaylist,
  playlistId,
  playlistName,
  playlistCover,
  songsAdded,
  exitEditMode,
} = usePlaylistEditMode();

function viewPlaylist(): void {
  if (playlistId.value) {
    router.push(`/playlist/${playlistId.value}`);
  }
}
</script>

<style scoped>
.playlist-edit-badge {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0; /* Allow shrinking */
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.playlist-edit-badge__pill {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0; /* Allow shrinking */
  gap: 6px;
  /* Orange/amber gradient to differentiate from purple Acting badge */
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
  transition: all 0.2s ease;
}

.playlist-edit-badge__pill:hover {
  box-shadow: 0 3px 12px rgba(245, 158, 11, 0.4);
  transform: translateY(-1px);
}

.playlist-edit-badge__cover {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.playlist-edit-badge__icon {
  flex-shrink: 0;
  opacity: 0.95;
}

.playlist-edit-badge__text {
  flex: 1;
  min-width: 0; /* Allow shrinking */
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.playlist-edit-badge__text strong {
  font-weight: 600;
}

.playlist-edit-badge__prefix {
  /* Will be hidden on mobile via media query */
}

.playlist-edit-badge__count {
  font-size: 11px;
  opacity: 0.9;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  white-space: nowrap;
}

.playlist-edit-badge__view {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  text-decoration: none;
}

.playlist-edit-badge__view:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.1);
}

.playlist-edit-badge__exit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  margin-left: 2px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.playlist-edit-badge__exit:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.playlist-edit-badge__exit:active {
  transform: scale(0.95);
}

/* Responsive - optimize for mobile */
@media (max-width: 600px) {
  .playlist-edit-badge {
    justify-content: stretch; /* Fill available space */
    flex: 1;
  }

  .playlist-edit-badge__pill {
    flex: 1; /* Fill available space */
    max-width: 100%;
    gap: 6px;
    padding: 6px 10px;
  }

  .playlist-edit-badge__text {
    font-size: 12px;
    flex: 1;
  }

  /* Show "Editing:" prefix on mobile for context */
  .playlist-edit-badge__prefix {
    display: inline;
  }

  /* Show count badge on mobile - helpful feedback */
  .playlist-edit-badge__count {
    display: inline-flex;
    font-size: 10px;
    padding: 2px 5px;
  }

  /* Hide view button on mobile - can tap playlist name area */
  .playlist-edit-badge__view {
    display: none;
  }

  .playlist-edit-badge__cover {
    width: 20px;
    height: 20px;
  }

  .playlist-edit-badge__exit {
    width: 20px;
    height: 20px;
  }
}

/* Very small screens */
@media (max-width: 400px) {
  .playlist-edit-badge__pill {
    padding: 5px 8px;
    gap: 4px;
  }

  .playlist-edit-badge__text {
    font-size: 11px;
  }

  /* Hide prefix on very small screens to save space */
  .playlist-edit-badge__prefix {
    display: none;
  }

  .playlist-edit-badge__count {
    font-size: 9px;
    padding: 1px 4px;
  }

  .playlist-edit-badge__cover {
    width: 18px;
    height: 18px;
  }

  .playlist-edit-badge__exit {
    width: 18px;
    height: 18px;
  }
}
</style>
